import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { QuoteDoc } from "@/components/documents/Documents";
import { resolveImg } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Printer, FileText, CheckCircle2, Wine, PartyPopper, HelpCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";

const Centered = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center bg-cream px-6 text-center">{children}</div>
);

const FLAVORS = ["Sweet", "Citrus", "Spicy", "Tropical", "Coffee", "Fresh", "Elegant", "Zero-proof"];
const ALCOHOLS = ["Vodka", "Tequila", "Rum", "Gin", "Whiskey", "No preference"];
const EXTRAS = [
  ["smoked_cocktail", "Smoked cocktail presentation"],
  ["personalized_cups", "Personalized cups"],
  ["custom_menu_sign", "Custom menu sign"],
  ["kids_lemonade_bar", "Kids lemonade bar"],
  ["premium_garnish", "Premium garnish design"],
  ["beverage_serving", "Beer / wine / champagne / prosecco serving"],
];

const Chip = ({ active, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${active ? "bg-ink text-white border-ink" : "bg-white text-ink border-line hover:border-ink"}`}>
    {children}
  </button>
);

export default function QuoteView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [accepting, setAccepting] = useState(false);
  const [nudge, setNudge] = useState(false);
  const [mode, setMode] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [choices, setChoices] = useState({
    drink1: "", drink2: "", drink3: "", mocktail_needed: false, kids_needed: false, allergies: "", notes: "",
    preferred_flavors: [], alcohol_preferences: [], event_style: "", guest_preferences: "",
    extras: { smoked_cocktail: false, personalized_cups: false, custom_menu_sign: false, kids_lemonade_bar: false, premium_garnish: false, beverage_serving: false, other: "" },
  });

  const fetchData = () => api.get(`/public/quote/${token}`).then((r) => setData(r.data))
    .catch(() => setErr("This quote link is invalid or has expired. Please contact Drunk Fruit Mobile Bar."));

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [token]);
  useEffect(() => {
    if (data && window.location.hash === "#drinks") setTimeout(() => document.getElementById("drinks")?.scrollIntoView({ behavior: "smooth" }), 300);
    if (data && window.location.hash === "#accept") setTimeout(() => document.getElementById("accept")?.scrollIntoView({ behavior: "smooth" }), 300);
  }, [data]);

  if (err) return <Centered><div><img src="/logo-mark.png" alt="Drunk Fruit" className="h-14 mx-auto mb-4" /><p className="text-muted-foreground">{err}</p></div></Centered>;
  if (!data) return <Centered><p className="text-muted-foreground animate-pulse tracking-widest uppercase text-sm">Loading quote…</p></Centered>;

  const ev = data.event;
  const guide = data.selection_guide;
  const guideUrl = guide?.url ? resolveImg(guide.url) : "";
  const accepted = ev.quote_status === "Accepted";
  const submitted = !!ev.client_choices;

  const toggleList = (key, val) => setChoices((c) => {
    const arr = c[key].includes(val) ? c[key].filter((x) => x !== val) : [...c[key], val];
    return { ...c, [key]: arr };
  });
  const setExtra = (k, v) => setChoices((c) => ({ ...c, extras: { ...c.extras, [k]: v } }));

  const acceptQuote = async () => {
    setAccepting(true);
    try {
      const { data: res } = await api.post(`/public/quote/${token}/accept`, { base_url: window.location.origin });
      await fetchData();
      if (!res.choices_submitted) { setNudge(true); setTimeout(() => document.getElementById("drinks")?.scrollIntoView({ behavior: "smooth" }), 200); }
      toast.success("Quote accepted — thank you!");
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setAccepting(false); }
  };

  const submitChoices = async () => {
    if (!mode) { toast.error("Please choose an option first"); return; }
    if (mode === "known" && !choices.drink1 && !choices.drink2 && !choices.drink3) { toast.error("Please enter at least one drink, or choose 'I need help choosing'"); return; }
    setSaving(true);
    try {
      await api.post(`/public/quote/${token}/menu-choices`, { choices: { ...choices, mode } });
      await fetchData();
      setNudge(false); setEditing(false);
      toast.success("Drink preferences submitted — thank you!");
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setSaving(false); }
  };

  const showForm = !submitted || editing;

  return (
    <div className="min-h-screen bg-cream py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6 no-print">
          <div className="flex items-center gap-3">
            <img src="/logo-mark.png" alt="Drunk Fruit" className="h-12 w-auto" />
            <div><div className="font-serif text-2xl leading-none">Your Event Quote</div><div className="text-xs text-muted-foreground">Review, print or save as PDF</div></div>
          </div>
          <div className="flex gap-2">
            {guideUrl && <a href={guideUrl} target="_blank" rel="noreferrer" className="btn-outline" data-testid="quoteview-guide"><FileText className="h-4 w-4" /> Selection Guide</a>}
            <button onClick={() => window.print()} className="btn-dark" data-testid="quoteview-print"><Printer className="h-4 w-4" /> Print / Save PDF</button>
          </div>
        </div>

        {/* ACCEPT QUOTE */}
        <div id="accept" className="df-card p-6 mb-6 no-print" data-testid="quoteview-accept-section">
          {accepted ? (
            <div className="flex items-start gap-3" data-testid="quoteview-accepted-msg">
              <CheckCircle2 className="h-6 w-6 text-olive shrink-0 mt-0.5" />
              <div>
                <h3 className="font-serif text-2xl mb-1">Thank you — your quote is accepted</h3>
                <p className="text-sm text-muted-foreground">Drunk Fruit will review your drink selections and send your contract, invoice / payment link, and next steps.</p>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h3 className="font-serif text-2xl mb-1 flex items-center gap-2"><PartyPopper className="h-5 w-5 text-olive" /> Ready to move forward?</h3>
                <p className="text-sm text-muted-foreground max-w-xl">Accepting the quote lets us start preparing your event. We'll review your drink choices, then send your contract, invoice / payment link and next steps.</p>
              </div>
              <Button onClick={acceptQuote} disabled={accepting} className="btn-dark shrink-0" data-testid="quoteview-accept-btn">
                <CheckCircle2 className="h-4 w-4" /> {accepting ? "Accepting…" : "Accept Quote"}
              </Button>
            </div>
          )}
          {nudge && !submitted && (
            <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 p-3 text-sm" data-testid="quoteview-accept-nudge">
              Before we prepare your contract, please tell us your preferred drinks below — or select “I need help choosing”.
            </div>
          )}
        </div>

        {/* CHOOSE YOUR DRINKS */}
        <div id="drinks" className="df-card p-6 mb-6 no-print" data-testid="quoteview-drinks-section">
          <h3 className="font-serif text-2xl mb-1 flex items-center gap-2"><Wine className="h-5 w-5 text-olive" /> Choose Your Drinks</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Please review the Cocktail &amp; Mocktail Selection Guide before submitting your drink choices. You may choose up to 3 signature cocktails or mocktails.
            If you are not sure, select “I need help choosing” and Drunk Fruit will help you build the menu.
          </p>
          {guideUrl && <a href={guideUrl} target="_blank" rel="noreferrer" className="btn-outline mb-5 inline-flex" data-testid="quoteview-guide-open"><FileText className="h-4 w-4" /> Open Cocktail &amp; Mocktail Selection Guide</a>}

          {submitted && !editing ? (
            <div className="rounded-xl bg-olive/10 border border-olive/30 p-4" data-testid="quoteview-drinks-received-msg">
              <p className="font-medium text-ink flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-olive" /> Your drink preferences have been received.</p>
              <p className="text-sm text-muted-foreground mt-1">Drunk Fruit will review them and confirm your final menu. Need to change something?</p>
              <Button onClick={() => { setEditing(true); setMode(ev.client_choices?.mode || ""); }} className="btn-outline mt-3" data-testid="quoteview-edit-choices">Edit my choices</Button>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-3 mb-5">
                <Chip active={mode === "known"} onClick={() => setMode("known")} testid="drinks-mode-known">I know my 3 drink choices</Chip>
                <Chip active={mode === "help"} onClick={() => setMode("help")} testid="drinks-mode-help"><HelpCircle className="h-4 w-4 inline mr-1" /> I need help choosing</Chip>
              </div>

              {mode === "known" && (
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2 grid sm:grid-cols-3 gap-4">
                    <div><Label className="df-label">Signature drink 1</Label><Input className="df-input" value={choices.drink1} onChange={(e) => setChoices({ ...choices, drink1: e.target.value })} data-testid="drink1" /></div>
                    <div><Label className="df-label">Signature drink 2</Label><Input className="df-input" value={choices.drink2} onChange={(e) => setChoices({ ...choices, drink2: e.target.value })} data-testid="drink2" /></div>
                    <div><Label className="df-label">Signature drink 3</Label><Input className="df-input" value={choices.drink3} onChange={(e) => setChoices({ ...choices, drink3: e.target.value })} data-testid="drink3" /></div>
                  </div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={choices.mocktail_needed} onCheckedChange={(v) => setChoices({ ...choices, mocktail_needed: !!v })} data-testid="mocktail-needed" /> Mocktail version needed?</label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={choices.kids_needed} onCheckedChange={(v) => setChoices({ ...choices, kids_needed: !!v })} data-testid="kids-needed" /> Kids drinks needed?</label>
                  <div className="sm:col-span-2"><Label className="df-label">Allergies or restrictions</Label><Input className="df-input" value={choices.allergies} onChange={(e) => setChoices({ ...choices, allergies: e.target.value })} data-testid="allergies" /></div>
                  <div className="sm:col-span-2"><Label className="df-label">Extra notes</Label><Textarea className="df-input" value={choices.notes} onChange={(e) => setChoices({ ...choices, notes: e.target.value })} data-testid="drinks-notes" /></div>
                </div>
              )}

              {mode === "help" && (
                <div className="space-y-4">
                  <div>
                    <Label className="df-label">Preferred flavors</Label>
                    <div className="flex flex-wrap gap-2">{FLAVORS.map((f) => <Chip key={f} active={choices.preferred_flavors.includes(f)} onClick={() => toggleList("preferred_flavors", f)} testid={`flavor-${f.toLowerCase()}`}>{f}</Chip>)}</div>
                  </div>
                  <div>
                    <Label className="df-label">Alcohol preferences</Label>
                    <div className="flex flex-wrap gap-2">{ALCOHOLS.map((a) => <Chip key={a} active={choices.alcohol_preferences.includes(a)} onClick={() => toggleList("alcohol_preferences", a)} testid={`alcohol-${a.toLowerCase().replace(/ /g, "-")}`}>{a}</Chip>)}</div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label className="df-label">Event style / theme</Label><Input className="df-input" value={choices.event_style} onChange={(e) => setChoices({ ...choices, event_style: e.target.value })} data-testid="event-style" /></div>
                    <div><Label className="df-label">Guest preferences</Label><Input className="df-input" value={choices.guest_preferences} onChange={(e) => setChoices({ ...choices, guest_preferences: e.target.value })} data-testid="guest-prefs" /></div>
                  </div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={choices.kids_needed} onCheckedChange={(v) => setChoices({ ...choices, kids_needed: !!v })} data-testid="kids-needed-help" /> Kids drinks needed?</label>
                  <div><Label className="df-label">Allergies or restrictions</Label><Input className="df-input" value={choices.allergies} onChange={(e) => setChoices({ ...choices, allergies: e.target.value })} data-testid="allergies-help" /></div>
                  <div><Label className="df-label">Notes</Label><Textarea className="df-input" value={choices.notes} onChange={(e) => setChoices({ ...choices, notes: e.target.value })} data-testid="drinks-notes-help" /></div>
                </div>
              )}

              {mode && (
                <div className="mt-6 border-t border-line pt-5">
                  <p className="df-label flex items-center gap-2"><Sparkles className="h-4 w-4 text-olive" /> Want to add extras? (optional)</p>
                  <div className="grid sm:grid-cols-2 gap-2 mt-1">
                    {EXTRAS.map(([k, label]) => (
                      <label key={k} className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={choices.extras[k]} onCheckedChange={(v) => setExtra(k, !!v)} data-testid={`extra-${k}`} /> {label}</label>
                    ))}
                  </div>
                  <div className="mt-3"><Label className="df-label">Other request</Label><Input className="df-input" value={choices.extras.other} onChange={(e) => setExtra("other", e.target.value)} data-testid="extra-other" /></div>
                </div>
              )}

              {mode && (
                <Button onClick={submitChoices} disabled={saving} className="btn-dark mt-6" data-testid="submit-drinks-btn">
                  {saving ? "Submitting…" : "Submit Drink Choices"}
                </Button>
              )}
            </>
          )}
        </div>

        <div className="print-area rounded-2xl overflow-hidden shadow-lg">
          <QuoteDoc e={ev} pricing={data.pricing} guideUrl={guideUrl} />
        </div>
      </div>
    </div>
  );
}
